// Per-endpoint retry policy.
//
// Retry safety is decided by IDEMPOTENCY, not by convenience:
//
//   Retried  - the writes that carry an Idempotency-Key (events, errors). The
//              backend replays its original answer for a repeated key, so a
//              retry whose first response was lost cannot double-count.
//   Once more - the reads (config bootstrap, sdk-config, ad breaks): one retry
//              avoids a spurious failure on a blip without hiding an outage.
//   Never    - /v1/tracking/relay folds straight into counters with no key, so
//              a duplicate would inflate them; heartbeat is superseded by the
//              next beat anyway.
//
// A 4xx is never retried regardless of endpoint: the request itself was
// rejected, so an identical retry fails identically.
const NO_RETRY = 0;

const POLICIES = Object.freeze({
  '/api/v1/config': { maxAttempts: 2 },
  'sdk-config': { maxAttempts: 2 },
  '/v1/adbreaks/resolve': { maxAttempts: 2 },

  '/api/v1/events': { maxAttempts: 3 },
  '/api/v1/errors': { maxAttempts: 2 },
  '/v1/tracking/relay': { maxAttempts: 1 },
  '/api/v1/heartbeat': { maxAttempts: 1 },
});

const DEFAULT_POLICY = { maxAttempts: 1 };
const BASE_BACKOFF_MS = 250;

export class RetryPolicy {
  #policies;

  constructor(overrides = {}) {
    this.#policies = { ...POLICIES, ...overrides };
  }

  maxAttempts(key) {
    return (this.#policies[key] ?? DEFAULT_POLICY).maxAttempts;
  }

  // The one question a caller asks. Both conditions must hold: the endpoint
  // must permit another attempt, AND the failure must be one that could
  // plausibly succeed on a retry.
  shouldRetry(key, error, attempt) {
    if (attempt >= this.maxAttempts(key)) return false;
    return error?.recoverable === true;
  }

  // Exponential backoff. No jitter: these are low-volume calls from a single
  // widget, not a fleet reconnecting in lockstep, so the added complexity
  // would not buy anything measurable.
  delayFor(attempt) {
    return attempt <= 0 ? NO_RETRY : BASE_BACKOFF_MS * 2 ** (attempt - 1);
  }
}
